import { api } from "./api";

/**
 * Financial reports, derived from what the app already records.
 *
 * NB: `ledgerService` is a different thing — it serves one party's running
 * account on the buyer/supplier ledger screens. This is the farm's books.
 *
 * There is no vouchers table and no ledgers table. Every figure comes from
 * sales, payables, receivables, receipts, payments, expenses and stock, mapped
 * onto Tally's own account groups at read time — so the numbers can be checked
 * against the books the farm already keeps, line by line.
 *
 * The report is EXPECTED not to balance, and `gaps` says why. That is the
 * point: the difference measures how much of the farm's money lives outside
 * this app, which is the specification for what still has to be built.
 */

/** Which statement an account lands on, and which side a positive sits. */
export type LedgerNature = "A" | "L" | "I" | "E";

export interface TrialBalanceLine {
  account: string;
  group_id: string;
  group: string;
  nature: LedgerNature;
  /** Both non-negative; normally exactly one is non-zero. */
  debit: number;
  credit: number;
  /** One sentence saying what was counted, so a difference can be explained. */
  derivation: string;
  /**
   * Calculated rather than posted: the closing stock valuation, opening stock
   * charged to trading, profit brought forward. Not the difference between
   * the columns — see TrialBalance.stock_valuation.
   */
  worked_out?: boolean;
}

/** One thing the app does not model, and so one reason the report won't balance. */
export interface TrialBalanceGap {
  name: string;
  detail: string;
  /** Absent when the app cannot know the amount. */
  amount?: number;
}

// One account where the CA's opening figure for the year and the app's own
// carried-forward position disagree (debit-positive).
export interface OpeningDifference {
  account: string;
  ca: number;
  app: number;
  difference: number;
}

export interface TrialBalance {
  from: string;
  to: string;
  lines: TrialBalanceLine[];
  total_debit: number;
  total_credit: number;
  /** total_debit − total_credit. Expected to be non-zero; not an error. */
  difference: number;
  /**
   * What the difference is made of: the closing stock valuation (a valuation,
   * not a voucher, so nothing on the credit side matches it) plus, for a year
   * whose typed opening set did not post, typed opening stock less the app's
   * own. difference = stock_valuation + opening_stock_gap.
   */
  stock_valuation?: number;
  opening_stock_gap?: number;
  gaps: TrialBalanceGap[];
  opening_check?: OpeningDifference[];
}

export const NATURE_LABEL: Record<LedgerNature, string> = {
  A: "Asset",
  L: "Liability",
  I: "Income",
  E: "Expense",
};

/** One account on a statement, with the side already resolved to one figure. */
export interface StatementLine {
  account: string;
  group: string;
  amount: number;
  derivation: string;
}

export interface ProfitAndLoss {
  from: string;
  to: string;
  trading_income: StatementLine[];
  trading_expense: StatementLine[];
  closing_stock: number;
  /** Always true: every period pairs closing stock with its opening stock — see the note. */
  closing_stock_included: boolean;
  closing_stock_note?: string;
  gross_profit: number;
  indirect_income: StatementLine[];
  indirect_expense: StatementLine[];
  net_profit: number;
  /**
   * The same gaps the trial balance carries. The biggest of them — payroll,
   * depreciation, loan interest — are all EXPENSES, so this net profit is
   * optimistic by an amount the app cannot state.
   */
  gaps: TrialBalanceGap[];
}

export interface BalanceSheet {
  as_of: string;
  liabilities: StatementLine[];
  assets: StatementLine[];
  /** The period's result, carried to the credit side. */
  net_profit: number;
  total_liabilities: number;
  total_assets: number;
  /** assets − liabilities. Expected to be large: no capital, no opening balances. */
  difference: number;
  gaps: TrialBalanceGap[];
  /**
   * Balances that say money is missing from the books: cash in hand below
   * zero, a bank account overdrawn. Shown above the statement.
   */
  warnings?: TrialBalanceGap[];
  opening_check?: OpeningDifference[];
}

export const financialReportsService = {
  /**
   * The trial balance. Defaults, server-side, to the Indian financial year to
   * date — 1 April to today — because that is the period the farm's books are
   * kept in and the one a CA will ask for.
   */
  trialBalance: (from?: string, to?: string) => {
    const q = new URLSearchParams();
    if (from) q.set("from", from);
    if (to) q.set("to", to);
    const qs = q.toString();
    return api
      .get<TrialBalance>(`/ledger/trial-balance${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },

  profitAndLoss: (from?: string, to?: string) =>
    api
      .get<ProfitAndLoss>(`/ledger/profit-and-loss${periodQuery(from, to)}`)
      .then((r) => r.data),

  /**
   * Takes the same PERIOD as the P&L, not just a date: the sheet carries the
   * period's net profit to the credit side, so it has to know which period.
   */
  balanceSheet: (from?: string, to?: string) =>
    api
      .get<BalanceSheet>(`/ledger/balance-sheet${periodQuery(from, to)}`)
      .then((r) => r.data),
};

function periodQuery(from?: string, to?: string): string {
  const q = new URLSearchParams();
  if (from) q.set("from", from);
  if (to) q.set("to", to);
  const qs = q.toString();
  return qs ? `?${qs}` : "";
}

/** Indian grouping, no decimals — these are whole-rupee report figures. */
export function reportInr(n: number): string {
  // Rounded on the magnitude, so a figure and its negative read alike:
  // Math.round(-85755.5) is -85755 while Math.round(85755.5) is 85756, and the
  // P&L showed the same gross loss as both (live walk 2, W2-13). The sign sits
  // outside the symbol, as formatSignedINR does: "-₹85,756", not "₹-85,756".
  const r = Math.round(Math.abs(n));
  return `${n < 0 && r !== 0 ? "-" : ""}₹${r.toLocaleString("en-IN")}`;
}

/**
 * Lines grouped in the order the chart of groups declares, so a printed trial
 * balance reads balance-sheet-then-P&L the way an accountant expects — rather
 * than in whatever order the server happened to append them.
 */
export function groupOrder(lines: TrialBalanceLine[]): string[] {
  const seen: string[] = [];
  for (const l of lines) if (!seen.includes(l.group)) seen.push(l.group);
  return seen;
}
